import { z } from 'zod';
import {
  type IntegrationScopeSchema,
  NangoConnectionConfigParamsSchema,
} from './integrations/integration.models.nango_auth';
import {
  ConnectionOrSubscriptionMetaPropertiesSchema,
  ExtendableProviderOperationInOrderSchema,
  ProviderFormFieldSchema,
  ProviderIdSchema,
  ProviderImportProfileSchema,
  SyncSettingConfigSchema,
} from './integrations/integration.models.operations';
import { ConnectionReadSchema } from './models.connections';
import { GenericAccountId } from './models.generic';
import { UserProfileSchema } from './models.users';

/**
 * Nango Session Token Query Schema - for authentication endpoints
 */
export const GetNangoSessionTokenQuerySchema = NangoConnectionConfigParamsSchema.pick({
  accountId: true,
  connectionId: true,
  scope: true,
  mode: true,
});

export const GetNangoTokenParamsSchema = z.object({
  providerId: ProviderIdSchema,
});

export type NangoSessionTokenQuery = z.infer<typeof GetNangoSessionTokenQuerySchema> &
  z.infer<typeof GetNangoTokenParamsSchema>;

/**
 * Integration Operation Schema - uses the full ExtendableProviderOperationInOrderSchema with embedded settings
 * Updated to support unified format with embedded sync settings
 */
export const IntegrationOperationSchema = ExtendableProviderOperationInOrderSchema.extend({
  // Server-added execution metadata
  last_run: ConnectionOrSubscriptionMetaPropertiesSchema.pick({
    when: true,
    error: true,
    executionSeconds: true,
  }).nullable(),
}).describe('Integration operation with embedded settings and execution history');

export type IntegrationOperation = z.infer<typeof IntegrationOperationSchema>;

/**
 * Integration Connection Schema - aligned with ConnectionReadSchema
 * Renamed from ProviderConnectionSchema to reflect integration-focused terminology
 */
export const IntegrationConnectionSchema = ConnectionReadSchema.pick({
  id: true,
  account_id: true,
  nango_connection_id: true,
  created_at: true,
  updated_at: true,
  settings: true,
  imported_data_access: true,
  disconnected_at: true,
  disconnection_reason: true,
})
  .extend({
    connected: z.boolean(),
    current_user_can_manage: z.boolean(),
    managing_user: UserProfileSchema.omit({ timely_id: true }).nullable(),
  })
  .describe('Connection information for integration views');

/**
 * Integration Scope Schema for listing (slim version)
 * Renamed from ProviderScopeListSchema to reflect integration-focused terminology
 */
export const IntegrationScopeListSchema = z.object({
  supported: z.boolean().describe('Whether this scope is supported by the integration'),
  connection: IntegrationConnectionSchema.nullable().describe(
    'Connection information, null if no connection exists'
  ),
  has_synchronized: z
    .boolean()
    .optional()
    .describe('Whether all synchronize operations have been run successfully'),
});

/**
 * Integration Scope Schema with full details (includes operations)
 * Renamed from ProviderScopeDataSchema to reflect integration-focused terminology
 */
export const IntegrationScopeDataSchema = IntegrationScopeListSchema.extend({
  has_initialized: z
    .boolean()
    .describe('Whether all initialize operations have been run successfully'),
  has_synchronized: z
    .boolean()
    .describe('Whether all synchronize operations have been run successfully'),
  operations: z
    .array(IntegrationOperationSchema)
    .describe('Available operations with execution history'),
  provider_config_key: z
    .string()
    .describe('Provider configuration key for Nango connection (provider-scope)'),
  oauth_scopes: z
    .array(
      z.object({
        key: z.string().describe('OAuth scope identifier'),
        description: z.string().describe('Human-readable description of the permission'),
      })
    )
    .describe('OAuth scopes required for this scope'),
  // Server-computed webhook status
  supports_webhooks: z.boolean().describe('Whether webhooks are supported for this scope'),
}).describe('Integration data for a specific scope (personal or account)');

/**
 * Base schema for integrations
 * Renamed from ProviderIntegrationBaseSchema to remove redundant "Provider" prefix
 */
export const IntegrationBaseSchema = z.object({
  id: ProviderIdSchema.describe('Provider ID (same as tool.id)'),
  title: z.string().describe('Integration display name'),
  description: z.string().nullable().describe('Integration description'),
  icon_url: z.string().nullable().describe('Integration icon URL'),
  color: z.string().nullable().describe('Integration brand color'),
  experimental: z.boolean().describe('Whether this integration is experimental'),
});

/**
 * Integration Schema for listing (slim version)
 * Renamed from ProviderIntegrationSchema to remove redundant "Provider" prefix
 */
export const IntegrationSchema = IntegrationBaseSchema.extend({
  personal: IntegrationScopeListSchema.nullable().describe(
    'Personal scope data, null if not supported'
  ),
  account: IntegrationScopeListSchema.nullable().describe(
    'Account scope data, null if not supported'
  ),
}).describe('Integration listing with connection status for both scopes');

/**
 * Integration Details Schema (includes operations and configuration)
 * Renamed from ProviderIntegrationDetailsSchema to remove redundant "Provider" prefix
 */
export const IntegrationDetailsSchema = IntegrationBaseSchema.extend({
  personal: IntegrationScopeDataSchema.nullable().describe(
    'Personal scope data with operations, null if not supported'
  ),
  account: IntegrationScopeDataSchema.nullable().describe(
    'Account scope data with operations, null if not supported'
  ),
  // Account-scoped sync settings, resolved from either the provider's top-level
  // `settings` (fan-out framework providers) or its operation-embedded
  // `operationsInOrder[].settings` (legacy providers). The frontend reads this single
  // list regardless of provider plane; the per-connection enabled state is derived by
  // combining these declarations with `connection.settings`.
  sync_settings: z
    .array(SyncSettingConfigSchema)
    .describe('Account-scoped sync settings declared by the provider'),
  // Provider metadata
  framework: z
    .boolean()
    .optional()
    .describe(
      'Whether the provider runs on the fan-out sync framework (sync progress comes from sync_run) rather than the legacy per-operation pipeline'
    ),
  single_root_resource: z.boolean().describe('Whether this provider has a single root resource'),
  url_homepage: z.string().optional().describe('Homepage URL for the provider'),
  url_support: z.string().optional().describe('Support URL for the provider'),
  // Provider configuration data
  form_fields: z.array(ProviderFormFieldSchema).describe('Form fields required for provider setup'),
  import_profiles: z
    .array(ProviderImportProfileSchema)
    .describe('Selectable import profiles with display metadata'),
  // Provider webhook capabilities
  supports_personal_webhooks: z
    .boolean()
    .describe('Whether provider supports webhooks for personal connections'),
  supports_account_webhooks: z
    .boolean()
    .describe('Whether provider supports webhooks for account connections'),
}).describe('Complete integration details with configuration and setup data');

export type Integration = z.infer<typeof IntegrationSchema>;
export type IntegrationDetails = z.infer<typeof IntegrationDetailsSchema>;

/**
 * Query Schemas for Integration endpoints
 * Updated to use new naming conventions
 */
export const ListIntegrationsQuerySchema = z
  .object({
    accountId: GenericAccountId.describe('Account ID to get integrations for'),
  })
  .describe('Query parameters for listing integrations');

export const GetIntegrationDetailsParamsSchema = z
  .object({
    providerId: ProviderIdSchema.describe('Provider identifier'),
  })
  .describe('Path parameters for getting integration details');

export const GetIntegrationDetailsQuerySchema = z
  .object({
    accountId: GenericAccountId.describe('Account ID to get integration details for'),
  })
  .describe('Query parameters for getting integration details');

export type ListIntegrationsQuery = z.infer<typeof ListIntegrationsQuerySchema>;
export type GetIntegrationDetailsParams = z.infer<typeof GetIntegrationDetailsParamsSchema>;
export type GetIntegrationDetailsQuery = z.infer<typeof GetIntegrationDetailsQuerySchema>;

// Type aliases for server-side sync status computation utility
// Note: SyncSettingStatus now derived from embedded operation settings
export type SyncSettingStatus = Omit<z.infer<typeof SyncSettingConfigSchema>, 'key'> & {
  type: z.infer<typeof SyncSettingConfigSchema>['key'];
  scope: z.infer<typeof IntegrationScopeSchema>;
  title?: string;
  description?: string;
  required?: boolean;
  supported: boolean;
  enabled: boolean;
};
export type WebhookStatus = {
  supported: boolean;
  installed: boolean;
  active: boolean;
  count: number;
  lastActivity: string | null;
};
export type ComprehensiveSyncStatus = {
  settings: SyncSettingStatus[];
  counts: {
    total: number;
    supported: number;
    enabled: number;
    disabled: number;
    unsupported: number;
  };
  webhooks: WebhookStatus;
  syncMode: 'on_demand' | 'continuous' | null;
  setupCompleted: boolean;
};
